import type { Document } from "@contentful/rich-text-types";
import { Button } from "@ui/components/forms/button/Button";
import { Flex } from "@ui/components/layout/flex/Flex";
import { Form, useFormikContext } from "formik";

import { FORM_ID } from "@components/layouts/footer/footer-email-subscription/constants";
import type { Header, Values } from "@components/layouts/footer/footer-email-subscription/types";
import { fieldStateHelper } from "@lib/utils/form/fieldStateHelper";
import { FooterInput } from "../Input";
import { PolicyAgreement } from "../policy-agreement";
import { styles } from "./ExtendedForm.style";
import { getRequiredLabel } from "@components/layouts/footer/footer-email-subscription/utils/getRequiredLabel";
import { useEffect } from "react";

type Props = {
    headers: Header;
    isLoading: boolean;
    policyAgreement: Document;
    name?: string;
    email?: string;
};

export const ExtendedForm = ({ headers, isLoading, policyAgreement, email, name }: Props) => {
    const { errors, touched, setFieldValue } = useFormikContext<Values>();

    const { subscribeButton, emailLabel, nameLabel, checkboxText, discountDisclaimer } = headers;

    const { getFieldStateProps } = fieldStateHelper({
        errors,
        touched,
    });

    useEffect(() => {
        setFieldValue("email", email ?? "");
        setFieldValue("name", name ?? "");
    }, [name, email]);

    return (
        <Form id={FORM_ID} noValidate>
            <Flex {...styles.wrapper}>
                <Flex {...styles.inputsWrapper}>
                    <FooterInput
                        isDisabled={Boolean(email)}
                        label={getRequiredLabel(emailLabel)}
                        name="email"
                        styles={{
                            field: styles.fieldStyle,
                            errorMessage: styles.formErrorMessage,
                            label: styles.label,
                            errorField: styles.fieldErrorStyle,
                        }}
                        {...getFieldStateProps("email")}
                    />

                    <FooterInput
                        isDisabled={Boolean(name)}
                        label={getRequiredLabel(nameLabel)}
                        name="name"
                        styles={{
                            field: styles.fieldStyle,
                            errorMessage: styles.formErrorMessage,
                            label: styles.label,
                            errorField: styles.fieldErrorStyle,
                        }}
                        {...getFieldStateProps("name")}
                    />
                </Flex>

                <Button
                    isLoading={isLoading}
                    size="md"
                    variant="outline"
                    type="submit"
                    {...styles.button}
                >
                    {subscribeButton}
                </Button>

                <PolicyAgreement
                    checkboxText={checkboxText}
                    discountDisclaimer={discountDisclaimer}
                    error={errors.policy}
                    touched={touched.policy}
                    policyAgreement={policyAgreement}
                />
            </Flex>
        </Form>
    );
};
